import { useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import type { CrmContact } from "../../lib/crm-types";
import { Button } from "../ui/Button";
import { cn } from "../ui/cn";
import { ActivityComposer, type ComposerMode } from "./ActivityComposer";
import { TaskShortcuts } from "./TaskShortcuts";
import {
  canStillEdit,
  TIMELINE_FILTERS as FILTERS,
  TimelineEntryRow,
} from "./TimelineEntryRow";

/**
 * Everything that has ever happened with this company.
 *
 * Tickets appear beside notes, calls, meetings, quotes and tasks even though
 * they live in a different table and belong to a different product -- because
 * the history of an account *is* mostly its tickets, and a CRM that showed only
 * the parts a rep typed by hand would be a worse record than the channel.
 *
 * The rows themselves live in `TimelineEntryRow`, shared with the Helpdesk's
 * activity drawer.
 */
export function Timeline({
  companyId,
  companyName,
  contacts,
  taskBlockedReason,
  branchCount,
  composeMode,
  onComposeMode,
}: {
  companyId: number;
  /** Passed straight through to the composer, which words a task with it. */
  companyName: string;
  contacts: CrmContact[];
  /** Why tasks are unavailable, or null when they are not. */
  taskBlockedReason: string | null;
  /** Non-zero means this is a group, which can widen the view to its branches. */
  branchCount: number;
  /**
   * Held by the page so the header's quick actions open this composer rather
   * than each button sprouting a dialog of its own.
   */
  composeMode: ComposerMode | null;
  onComposeMode: (mode: ComposerMode | null) => void;
}) {
  const [filter, setFilter] = useState("all");
  /*
   * Off by default even on a group, per the rule that a group leads with its
   * directory: nine hundred depots' activity is a firehose somebody has to ask
   * for, not the first thing they should be shown.
   */
  const [includeBranches, setIncludeBranches] = useState(false);
  const queryClient = useQueryClient();

  const kinds = FILTERS.find((entry) => entry.id === filter)?.kinds ?? [];

  const timeline = useInfiniteQuery({
    queryKey: queryKeys.crmTimeline(companyId, kinds, includeBranches),
    queryFn: ({ pageParam }) =>
      crmApi.timeline(companyId, {
        kinds,
        cursor: pageParam as string | null,
        includeBranches,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 15_000,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["crm", "timeline", companyId] });
  };

  const edit = useMutation({
    mutationFn: (input: {
      id: string;
      patch: { subject?: string | null; body?: string | null };
    }) => crmApi.editActivity(Number(input.id.slice(2)), input.patch),
    onSuccess: refresh,
  });

  /*
   * Who is reading, so the page only offers Edit on entries this person wrote.
   * Cached across the app, so this is the same request the shell already made.
   */
  const me = useQuery({
    queryKey: queryKeys.me,
    queryFn: api.me,
    staleTime: 5 * 60_000,
  });

  const entries = timeline.data?.pages.flatMap((page) => page.entries) ?? [];

  /*
   * Re-read on every render rather than held in state. The window shuts while
   * somebody is looking at the page, and a stale `now` would leave Edit on
   * screen after the server had started refusing it -- the button reappears or
   * vanishes the next time anything re-renders, which is often enough.
   */
  const now = Date.now();

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ActivityComposer
        companyId={companyId}
        companyName={companyName}
        contacts={contacts}
        taskBlockedReason={taskBlockedReason}
        onDone={refresh}
        mode={composeMode}
        onModeChange={onComposeMode}
      />

      {/* One row that swipes on a phone, where nine chips wrapped to three
          rows before the first entry; wrapped from `md` up, where they fit. */}
      <div className="flex gap-1 overflow-x-auto border-b border-line px-4 py-2 [scrollbar-width:none] md:flex-wrap">
        {FILTERS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => setFilter(entry.id)}
            className={cn(
              "shrink-0 rounded-full px-2.5 py-1 text-xs transition-colors",
              filter === entry.id
                ? "bg-brand-600 text-white"
                : "text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
            )}
          >
            {entry.label}
          </button>
        ))}

        {branchCount > 0 ? (
          <label
            className="ml-auto flex shrink-0 items-center gap-1.5 text-xs text-muted"
            title="Show what is happening at every branch, not just this record"
          >
            <input
              type="checkbox"
              checked={includeBranches}
              onChange={(event) => setIncludeBranches(event.target.checked)}
              className="accent-brand-600"
            />
            Include branches
          </label>
        ) : null}
      </div>

      {/*
        Only on Tasks, and above the list rather than inside it. These are work
        that has not been raised yet; mixing them into a history of things that
        happened would make the timeline lie. On "all" they would also be five
        rows of future between somebody and the note they came to read.
      */}
      {filter === "tasks" ? (
        <TaskShortcuts
          companyId={companyId}
          taskBlockedReason={taskBlockedReason}
          onRaised={refresh}
        />
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {timeline.isLoading ? (
          <p className="py-8 text-center text-xs text-faint">Loading…</p>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <p className="text-xs text-faint">
              {EMPTY_TEXT[filter] ??
                "Nothing here yet. Add a note, or log the call you just had."}
            </p>
            {/* The action the view is filtered to. A "Tasks" tab that is empty
                and offers nothing is a dead end. */}
            {filter === "tasks" && taskBlockedReason ? (
              <p className="max-w-sm text-[11px] text-sla-warning">
                {taskBlockedReason}
              </p>
            ) : null}
            {FILTER_ACTION[filter] ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={
                  FILTER_ACTION[filter] === "task" && taskBlockedReason !== null
                }
                title={
                  FILTER_ACTION[filter] === "task"
                    ? (taskBlockedReason ?? undefined)
                    : undefined
                }
                onClick={() => onComposeMode(FILTER_ACTION[filter]!)}
              >
                {FILTER_ACTION_LABEL[filter]}
              </Button>
            ) : null}
          </div>
        ) : (
          /*
            A grid, not a stack, because the kind badges have to agree on a
            width across rows and only their common parent can decide that --
            likewise the times down the right. The outer columns take the
            widest badge and the widest timestamp on screen; each row picks the
            sizing up again with `grid-cols-subgrid`. See TimelineEntryRow.
          */
          <ol className="grid grid-cols-[max-content_1fr_max-content] gap-3">
            {entries.map((entry) => (
              <TimelineEntryRow
                key={entry.id}
                entry={entry}
                onEdit={
                  canStillEdit(entry, me.data?.id ?? null, now)
                    ? (patch) => edit.mutate({ id: entry.id, patch })
                    : undefined
                }
              />
            ))}
          </ol>
        )}

        {timeline.hasNextPage ? (
          <div className="py-4 text-center">
            <Button
              size="sm"
              variant="secondary"
              disabled={timeline.isFetchingNextPage}
              onClick={() => void timeline.fetchNextPage()}
            >
              {timeline.isFetchingNextPage ? "Loading…" : "Older"}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** What an empty filtered timeline offers, so no tab is a dead end. */
const FILTER_ACTION: Record<string, ComposerMode | undefined> = {
  all: "note",
  notes: "note",
  calls: "call",
  meetings: "meeting",
  commercial: "quote",
  tasks: "task",
};

const FILTER_ACTION_LABEL: Record<string, string> = {
  all: "Add a note",
  notes: "Add a note",
  calls: "Log a call",
  meetings: "Record a meeting",
  commercial: "Record a quote",
  tasks: "Add a task",
};

const EMPTY_TEXT: Record<string, string> = {
  tickets: "No tickets from this company yet.",
  tasks: "No tasks raised against this company.",
  changes: "Nothing has been changed on this record yet.",
};
